import { createSlice } from '@reduxjs/toolkit';
import type { ActionReducerMapBuilder } from '@reduxjs/toolkit';
import type { FlagIndicator, FlagSummaryState } from 'src/types/jobs';
import { fetchFlagSummary } from 'src/redux/flag-summary/api';

/**
 * Raw JSON body returned by `GET /api/flags/summary`. Every field is optional
 * so the reducer stays tolerant of an undefined / partial payload.
 */
export interface FlagSummaryPayload {
  flagged_work_order_count?: number;
  flagged_work_order_job_ids?: number[];
  open_flagged_work_order_count?: number;
  escalated_work_order_count?: number;
  // Additive map (SAAS-815 BBR): work_order_job_id -> active flag indicators
  // (Build Back Risk + Escalated), pre-sorted oldest-first by the BE.
  indicators?: Record<number, FlagIndicator[]>;
}

export const initialState: FlagSummaryState = {
  flaggedWorkOrderJobIds: [],
  flaggedWorkOrderCount: 0,
  openFlaggedWorkOrderCount: 0,
  escalatedWorkOrderCount: 0,
  indicators: {},
  isLoading: false,
  hasLoaded: false,
};

const flagSummary = createSlice({
  name: 'flagSummary',
  initialState,
  reducers: {},
  extraReducers: (builder: ActionReducerMapBuilder<FlagSummaryState>) => {
    builder.addCase(fetchFlagSummary().pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fetchFlagSummary().fulfilled, (state, action) => {
      state.isLoading = false;
      state.hasLoaded = true;
      const payload = action?.payload as FlagSummaryPayload | undefined;
      const ids = payload?.flagged_work_order_job_ids;
      state.flaggedWorkOrderJobIds = (Array.isArray(ids) ? ids : []).map(Number);
      state.flaggedWorkOrderCount = payload?.flagged_work_order_count ?? 0;
      // Open-board-scoped count (SAAS-815 Issue B). Falls back to the global
      // count if the BE hasn't shipped the new field yet, so the Open Jobs
      // chip never reads 0 during the deploy window.
      state.openFlaggedWorkOrderCount =
        payload?.open_flagged_work_order_count
        ?? payload?.flagged_work_order_count
        ?? 0;
      state.escalatedWorkOrderCount = payload?.escalated_work_order_count ?? 0;
      // Tolerant of an absent map (e.g. pre-BE-deploy): default to empty.
      state.indicators = payload?.indicators ?? {};
    });
    builder.addCase(fetchFlagSummary().rejected, (state, action) => {
      // An ABORTED fetch must not touch hasLoaded: the thunk state is global, so
      // one component's cleanup abort would mark the summary "loaded" with empty
      // ids while another instance's fetch is still in flight — the flag filter
      // then flashes "No work orders match this filter" until the live fetch
      // resolves (SAAS-1060: loader ends -> false empty-state -> jobs pop in).
      const payload = action?.payload as { actionType?: string } | undefined;
      if (action?.meta?.aborted || payload?.actionType === 'canceled') {
        return;
      }
      state.isLoading = false;
      // Mark loaded on GENUINE failure so an active flag filter falls through to
      // its normal (possibly-empty) render instead of showing the loading state
      // forever (SAAS-1060 fail-open).
      state.hasLoaded = true;
    });
  },
});

export default flagSummary.reducer;
